import React, { FunctionComponent } from "react";
import styles from "./Video.module.scss";
import VideoBlockProps from "./VideoProps.interface";

import BackgroundMedia from "components/BackgroundMedia/BackgroundMedia";
import combineClasses from "helpers/combine-classes";
import getVideoAspectRatio from "app/media/helpers/getVideoAspectRatio";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import useAnchor from "blocks/hooks/useAnchor";
import useSplitBackground from "blocks/hooks/useSplitBackground";
import useWrapperStyles from "blocks/hooks/useWrapperStyles";
import VideoEmbed from "components/VideoEmbed/VideoEmbed";
import VideoViewer from "components/VideoViewer/VideoViewer";
import useAnimation from "blocks/hooks/useAnimation";
import setInViewClasses from "blocks/hooks/setInViewClasses";

const Video: FunctionComponent<VideoBlockProps> = (props): JSX.Element => {
    const wrapperClasses = getWrapperClasses(props.settings);
    const wrapperStyles = useWrapperStyles(props.settings);

    const identifier = useAnchor(props.settings.anchorMenuLabel);
    const { splitClasses, splitStyles } = useSplitBackground(props);
    const { animationClasses } = useAnimation(styles, "animation1");
    const { inViewElementRef, inViewClasses } = setInViewClasses(styles);

    const video = props.content?.video?.[0] && props.content?.video.length > 0 ? props.content.video[0].content : undefined;
    const videoAspectRatio = getVideoAspectRatio(video?.videoCrop, "Widescreen (16:9)");
    const videoSizeClass = props.settings.videoSize ? styles[props.settings.videoSize] : undefined;

    const isAutoplay = video && video.videoAutoplay ? video.videoAutoplay : false;
    const isLoop = video && video.videoLoop ? video.videoLoop : false;
    const showControls = !isAutoplay;

    return (
        <section className={combineClasses(styles.wrapper, animationClasses, inViewClasses, videoSizeClass, ...wrapperClasses)} style={{ ...wrapperStyles, ...splitStyles }} id={identifier} ref={inViewElementRef}>
            <div className={combineClasses(styles.container, videoSizeClass, ...splitClasses)}>
                <div className={styles.media}>
                    {/* Local Video */}
                    {video && video._contentType === "blockVideoNestedLocal" && video.video?.umbracoFile ? (
                        <VideoViewer
                            alt={props.content.videoAlternative}
                            aspectRatioX={videoAspectRatio.aspectRatioX}
                            aspectRatioY={videoAspectRatio.aspectRatioY}
                            autoplay={isAutoplay}
                            className={styles.video}
                            loadedClassName={styles.is_loaded}
                            controls={showControls}
                            key={props.contentUdi}
                            loop={isLoop}
                            poster={video?.poster?.umbracoFile?.src}
                            src={video.video?.umbracoFile} />
                    ) : null}

                    {/* Video Embed */}
                    {video && video._contentType === "blockVideoNestedYouTube" && video.videoUrl ? (
                        <VideoEmbed className={styles.video} key={props.contentUdi} src={video.videoUrl} alt={props.content.videoAlternative} autoplay={isAutoplay} loop={isLoop} aspectRatioX={16} aspectRatioY={9} />
                    ) : null}

                    {/* Caption */}
                    {props.content.videoCaption && props.content.videoCaption.length > 0 ? (
                        <div className={styles.caption}>{props.content.videoCaption}</div>
                    ) : null}
                </div>
            </div>

            {props.settings.backgroundMedia?.[0] && props.settings.backgroundMedia.length > 0 ? (
                <BackgroundMedia key={props.contentUdi} {...props.settings.backgroundMedia[0]} />
            ) : null}
        </section>
    );
};

export default Video;